import { supabase } from '@/integrations/supabase/client';
import { Project, SavedRig, SharedProject } from '@/types/project';
import type { Camera } from '@/data/cameras';
import type { AccessorySpec } from '@/data/accessories';
import type { RigStyle } from '@/types/rig';
import type { Json } from '@/integrations/supabase/types';

// Type helpers for database responses
interface DbRig {
  id: string;
  project_id: string;
  name: string;
  camera_id: string;
  camera_data: Json;
  rig_style: RigStyle;
  auto_added_cables: Json;
  created_at: string;
  updated_at: string;
}

// Version snapshot debouncing - module level state for singleton service
const VERSION_DEBOUNCE_DELAY = 3000; // 3 seconds
const versionTimeouts: Map<string, NodeJS.Timeout> = new Map();
const versionBaselines: Map<string, { accessoryCount: number; cameraId: string; rigStyle: string }> = new Map();

// Debounced version snapshot creation
const createDebouncedVersionSnapshot = (
  rigId: string,
  currentState: { accessoryCount: number; cameraId: string; rigStyle: string },
  changeType: string,
  changeSummary: string
) => {
  // Capture baseline on first change in burst
  if (!versionBaselines.has(rigId)) {
    versionBaselines.set(rigId, currentState);
  }
  
  // Clear existing timeout
  const existingTimeout = versionTimeouts.get(rigId);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }
  
  // Set new timeout
  const timeout = setTimeout(async () => {
    const baseline = versionBaselines.get(rigId);
    if (!baseline) return;
    
    // Calculate summary based on net change from baseline
    const netAccessoryChange = currentState.accessoryCount - baseline.accessoryCount;
    let finalChangeType = changeType;
    let finalSummary = changeSummary;
    
    if (changeType === 'accessories_changed') {
      if (netAccessoryChange > 0) {
        finalSummary = `Added ${netAccessoryChange} accessory(ies)`;
      } else if (netAccessoryChange < 0) {
        finalSummary = `Removed ${Math.abs(netAccessoryChange)} accessory(ies)`;
      } else {
        finalSummary = 'Modified accessories';
      }
    }
    
    try {
      await supabase.rpc('create_rig_version_snapshot', {
        p_rig_id: rigId,
        p_change_type: finalChangeType,
        p_change_summary: finalSummary,
      });
    } catch (err) {
      console.warn('Failed to create version snapshot:', err);
    }
    
    // Cleanup
    versionBaselines.delete(rigId);
    versionTimeouts.delete(rigId);
  }, VERSION_DEBOUNCE_DELAY);
  
  versionTimeouts.set(rigId, timeout);
};

// Convert database rig to app format
const dbRigToSavedRig = (dbRig: DbRig, accessories: AccessorySpec[]): SavedRig => ({
  id: dbRig.id,
  name: dbRig.name,
  camera: dbRig.camera_data as unknown as Camera,
  accessories,
  rigStyle: dbRig.rig_style,
  autoAddedCables: (dbRig.auto_added_cables as Record<string, string[]>) || {},
  createdAt: dbRig.created_at,
  updatedAt: dbRig.updated_at,
});

// Database storage service
export const dbStorage = {
  // Projects
  getProjects: async (userId: string): Promise<Project[]> => {
    const { data: projects, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching projects:', error);
      return [];
    }

    // Fetch all rigs for these projects
    const projectIds = projects.map(p => p.id);
    if (projectIds.length === 0) {
      return projects.map(p => ({ 
        id: p.id, 
        name: p.name, 
        rigs: [], 
        createdAt: p.created_at, 
        updatedAt: p.updated_at 
      }));
    }

    const { data: rigs, error: rigsError } = await supabase
      .from('rigs')
      .select('*')
      .in('project_id', projectIds);

    if (rigsError) {
      console.error('Error fetching rigs:', rigsError);
      return projects.map(p => ({ 
        id: p.id, 
        name: p.name, 
        rigs: [], 
        createdAt: p.created_at, 
        updatedAt: p.updated_at 
      }));
    }

    // Fetch all accessories for these rigs
    const rigIds = rigs?.map(r => r.id) || [];
    let rigAccessoriesMap: Record<string, AccessorySpec[]> = {};

    if (rigIds.length > 0) {
      const { data: accessories, error: accError } = await supabase
        .from('rig_accessories')
        .select('*')
        .in('rig_id', rigIds)
        .order('sort_order', { ascending: true });

      if (!accError && accessories) {
        rigAccessoriesMap = accessories.reduce((acc, item) => {
          if (!acc[item.rig_id]) acc[item.rig_id] = [];
          acc[item.rig_id].push(item.accessory_data as unknown as AccessorySpec);
          return acc;
        }, {} as Record<string, AccessorySpec[]>);
      }
    }

    // Build project objects with rigs
    const rigsMap: Record<string, SavedRig[]> = {};
    rigs?.forEach(rig => {
      const dbRig = rig as unknown as DbRig;
      if (!rigsMap[dbRig.project_id]) rigsMap[dbRig.project_id] = [];
      rigsMap[dbRig.project_id].push(
        dbRigToSavedRig(dbRig, rigAccessoriesMap[dbRig.id] || [])
      );
    });

    return projects.map(p => ({
      id: p.id,
      name: p.name,
      rigs: rigsMap[p.id] || [],
      createdAt: p.created_at,
      updatedAt: p.updated_at,
    }));
  },

  // Get shared projects (projects shared via team membership)
  getSharedProjects: async (userId: string): Promise<SharedProject[]> => {
    // Step 1: find teams the user belongs to
    const { data: teamMemberships, error: teamError } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('user_id', userId);

    if (teamError || !teamMemberships?.length) {
      return [];
    }

    const teamIds = teamMemberships.map(t => t.team_id);

    // Step 2: fetch shares for those teams (excluding user's own projects)
    const { data: sharedProjectData, error: sharesError } = await supabase
      .from('project_team_shares')
      .select(`
        id,
        project_id,
        permission,
        shared_by,
        projects!inner (
          id,
          name,
          user_id,
          created_at,
          updated_at
        )
      `)
      .in('team_id', teamIds)
      .neq('projects.user_id', userId);

    if (sharesError || !sharedProjectData?.length) {
      return [];
    }

    // Step 3: fetch member-level permission overrides for this user
    const shareIds = sharedProjectData.map(s => s.id);
    const { data: memberPermissions } = await supabase
      .from('project_member_permissions')
      .select('project_share_id, permission')
      .in('project_share_id', shareIds)
      .eq('user_id', userId);

    // Build a map of share_id -> member permission override
    const memberPermissionMap: Record<string, string> = {};
    memberPermissions?.forEach(mp => {
      memberPermissionMap[mp.project_share_id] = mp.permission;
    });

    const projectIds = [...new Set(sharedProjectData.map(s => s.project_id))];

    // Fetch rigs for these projects
    const { data: rigs } = await supabase
      .from('rigs')
      .select('*')
      .in('project_id', projectIds);

    const rigIds = rigs?.map(r => r.id) || [];
    let rigAccessoriesMap: Record<string, AccessorySpec[]> = {};

    if (rigIds.length > 0) {
      const { data: accessories } = await supabase
        .from('rig_accessories')
        .select('*')
        .in('rig_id', rigIds)
        .order('sort_order', { ascending: true });

      if (accessories) {
        rigAccessoriesMap = accessories.reduce((acc, item) => {
          if (!acc[item.rig_id]) acc[item.rig_id] = [];
          acc[item.rig_id].push(item.accessory_data as unknown as AccessorySpec);
          return acc;
        }, {} as Record<string, AccessorySpec[]>);
      }
    }

    // Build rigs map
    const rigsMap: Record<string, SavedRig[]> = {};
    rigs?.forEach(rig => {
      const dbRig = rig as unknown as DbRig;
      if (!rigsMap[dbRig.project_id]) rigsMap[dbRig.project_id] = [];
      rigsMap[dbRig.project_id].push(
        dbRigToSavedRig(dbRig, rigAccessoriesMap[dbRig.id] || [])
      );
    });

    // Build project objects from joined data with effective permission (member override > team default)
    const projectsMap = new Map<string, SharedProject>();
    sharedProjectData.forEach(share => {
      const p = share.projects as unknown as {
        id: string;
        name: string;
        user_id: string;
        created_at: string;
        updated_at: string;
      };

      // Determine effective permission: member override takes precedence over team default
      const memberOverride = memberPermissionMap[share.id];
      const effectivePermission = memberOverride || share.permission;

      // If a project is shared via multiple teams, keep the highest permission
      const existing = projectsMap.get(p.id);
      const permissionRank = { admin: 3, edit: 2, view: 1 };
      const existingRank = existing ? permissionRank[existing.permission as keyof typeof permissionRank] || 0 : 0;
      const newRank = permissionRank[effectivePermission as keyof typeof permissionRank] || 0;

      if (!existing || newRank > existingRank) {
        projectsMap.set(p.id, {
          id: p.id,
          name: p.name,
          rigs: rigsMap[p.id] || [],
          createdAt: p.created_at,
          updatedAt: p.updated_at,
          permission: effectivePermission as 'view' | 'edit' | 'admin',
          sharedBy: share.shared_by,
          sharedByName: undefined,
          projectOwnerId: p.user_id,
        });
      }
    });

    // Sort by most recently updated
    return Array.from(projectsMap.values()).sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  },

  getProject: async (projectId: string): Promise<Project | null> => {
    const { data: project, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .maybeSingle();

    if (error || !project) return null;

    // Fetch rigs
    const { data: rigs } = await supabase
      .from('rigs')
      .select('*')
      .eq('project_id', projectId);

    const rigIds = rigs?.map(r => r.id) || [];
    let rigAccessoriesMap: Record<string, AccessorySpec[]> = {};

    if (rigIds.length > 0) {
      const { data: accessories } = await supabase
        .from('rig_accessories')
        .select('*')
        .in('rig_id', rigIds)
        .order('sort_order', { ascending: true });

      if (accessories) {
        rigAccessoriesMap = accessories.reduce((acc, item) => {
          if (!acc[item.rig_id]) acc[item.rig_id] = [];
          acc[item.rig_id].push(item.accessory_data as unknown as AccessorySpec);
          return acc;
        }, {} as Record<string, AccessorySpec[]>);
      }
    }

    const savedRigs = rigs?.map(rig => {
      const dbRig = rig as unknown as DbRig;
      return dbRigToSavedRig(dbRig, rigAccessoriesMap[dbRig.id] || []);
    }) || [];

    return {
      id: project.id,
      name: project.name,
      rigs: savedRigs,
      createdAt: project.created_at,
      updatedAt: project.updated_at,
    };
  },

  createProject: async (userId: string, name: string): Promise<Project | null> => {
    const { data, error } = await supabase
      .from('projects')
      .insert({ user_id: userId, name })
      .select()
      .single();

    if (error) {
      console.error('Error creating project:', error);
      return null;
    }

    return {
      id: data.id,
      name: data.name,
      rigs: [],
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  updateProject: async (projectId: string, updates: { name?: string }): Promise<Project | null> => {
    const { error } = await supabase
      .from('projects')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', projectId);

    if (error) {
      console.error('Error updating project:', error);
      return null;
    }

    return dbStorage.getProject(projectId);
  },

  deleteProject: async (projectId: string): Promise<boolean> => {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    return !error;
  },

  // Rigs
  addRig: async (
    projectId: string, 
    rig: Omit<SavedRig, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<SavedRig | null> => {
    // Insert rig
    const { data: rigData, error: rigError } = await supabase
      .from('rigs')
      .insert({
        project_id: projectId,
        name: rig.name,
        camera_id: rig.camera.id,
        camera_data: rig.camera as unknown as Json,
        rig_style: rig.rigStyle,
        auto_added_cables: rig.autoAddedCables as unknown as Json,
      })
      .select()
      .single();

    if (rigError || !rigData) {
      console.error('Error creating rig:', rigError);
      return null;
    }

    // Insert accessories
    if (rig.accessories.length > 0) {
      const accessoryRows = rig.accessories.map((acc, index) => ({
        rig_id: rigData.id,
        accessory_id: acc.id,
        accessory_data: acc as unknown as Json,
        sort_order: index,
      }));

      const { error: accError } = await supabase
        .from('rig_accessories')
        .insert(accessoryRows);

      if (accError) {
        console.error('Error adding accessories:', accError);
      }
    }

    // Create initial version snapshot
    try {
      await supabase.rpc('create_rig_version_snapshot', {
        p_rig_id: rigData.id,
        p_change_type: 'created',
        p_change_summary: `Rig created with ${rig.camera.brand} ${rig.camera.name}`,
      });
    } catch (err) {
      console.warn('Failed to create initial version snapshot:', err);
    }

    // Update project's updated_at
    await supabase
      .from('projects')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', projectId);

    const dbRig = rigData as unknown as DbRig;
    return dbRigToSavedRig(dbRig, rig.accessories);
  },

  updateRig: async (
    projectId: string,
    rigId: string, 
    updates: Partial<Omit<SavedRig, 'id' | 'createdAt'>>,
    options?: { skipVersionSnapshot?: boolean }
  ): Promise<SavedRig | null> => {
    // Fetch current rig state to compare what actually changed
    const { data: currentRig } = await supabase
      .from('rigs')
      .select('camera_id, rig_style, name')
      .eq('id', rigId)
      .single();

    // Determine what ACTUALLY changed by comparing values
    const cameraChanged = updates.camera !== undefined && 
      currentRig?.camera_id !== updates.camera.id;
    const styleChanged = updates.rigStyle !== undefined && 
      currentRig?.rig_style !== updates.rigStyle;
    const nameChanged = updates.name !== undefined && 
      currentRig?.name !== updates.name;
    const accessoriesChanged = updates.accessories !== undefined;

    // Set change type based on what actually changed
    let changeType = 'updated';
    let changeSummary = 'Updated rig';
    
    if (cameraChanged) {
      changeType = 'camera_changed';
      changeSummary = `Changed camera to ${updates.camera!.brand} ${updates.camera!.name}`;
    } else if (styleChanged) {
      changeType = 'style_changed';
      changeSummary = `Changed rig style to ${updates.rigStyle}`;
    } else if (accessoriesChanged) {
      changeType = 'accessories_changed';
      changeSummary = `Updated accessories (${updates.accessories!.length} items)`;
    } else if (nameChanged) {
      changeType = 'renamed';
      changeSummary = `Renamed rig to "${updates.name}"`;
    }

    // Only create version snapshot if something meaningful changed
    const shouldCreateVersion = !options?.skipVersionSnapshot && 
      (cameraChanged || styleChanged || accessoriesChanged);
    
    if (shouldCreateVersion) {
      // Use debounced versioning for accessory changes (rapid add/remove)
      // Immediate versioning for camera and style changes (less frequent)
      if (accessoriesChanged && !cameraChanged && !styleChanged) {
        createDebouncedVersionSnapshot(
          rigId,
          { 
            accessoryCount: updates.accessories!.length, 
            cameraId: updates.camera?.id || currentRig?.camera_id || '',
            rigStyle: updates.rigStyle || currentRig?.rig_style || ''
          },
          changeType,
          changeSummary
        );
      } else {
        // Immediate snapshot for camera/style changes
        try {
          await supabase.rpc('create_rig_version_snapshot', {
            p_rig_id: rigId,
            p_change_type: changeType,
            p_change_summary: changeSummary,
          });
        } catch (err) {
          console.warn('Failed to create version snapshot:', err);
        }
      }
    }

    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.rigStyle !== undefined) updateData.rig_style = updates.rigStyle;
    if (updates.autoAddedCables !== undefined) updateData.auto_added_cables = updates.autoAddedCables as unknown as Json;
    if (updates.camera !== undefined) {
      updateData.camera_id = updates.camera.id;
      updateData.camera_data = updates.camera as unknown as Json;
    }

    const { data: rigData, error } = await supabase
      .from('rigs')
      .update(updateData)
      .eq('id', rigId)
      .select()
      .single();

    if (error || !rigData) {
      console.error('Error updating rig:', error);
      return null;
    }

    // Update accessories if provided
    if (updates.accessories !== undefined) {
      // Delete existing accessories
      await supabase
        .from('rig_accessories')
        .delete()
        .eq('rig_id', rigId);

      // Insert new accessories
      if (updates.accessories.length > 0) {
        const accessoryRows = updates.accessories.map((acc, index) => ({
          rig_id: rigId,
          accessory_id: acc.id,
          accessory_data: acc as unknown as Json,
          sort_order: index,
        }));

        await supabase
          .from('rig_accessories')
          .insert(accessoryRows);
      }
    }

    // Update project's updated_at
    await supabase
      .from('projects')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', projectId);

    // Fetch updated rig with accessories
    const { data: accessories } = await supabase
      .from('rig_accessories')
      .select('*')
      .eq('rig_id', rigId)
      .order('sort_order', { ascending: true });

    const dbRig = rigData as unknown as DbRig;
    return dbRigToSavedRig(
      dbRig, 
      accessories?.map(a => a.accessory_data as unknown as AccessorySpec) || []
    );
  },

  deleteRig: async (projectId: string, rigId: string): Promise<boolean> => {
    const { error } = await supabase
      .from('rigs')
      .delete()
      .eq('id', rigId);

    if (!error) {
      // Update project's updated_at
      await supabase
        .from('projects')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', projectId);
    }

    return !error;
  },

  duplicateRig: async (projectId: string, rigId: string): Promise<SavedRig | null> => {
    // Fetch the original rig
    const { data: rigData, error } = await supabase
      .from('rigs')
      .select('*')
      .eq('id', rigId)
      .single();

    if (error || !rigData) return null;

    // Fetch accessories
    const { data: accessories } = await supabase
      .from('rig_accessories')
      .select('*')
      .eq('rig_id', rigId)
      .order('sort_order', { ascending: true });

    const dbRig = rigData as unknown as DbRig;
    
    return dbStorage.addRig(projectId, {
      name: `${dbRig.name} (Copy)`,
      camera: dbRig.camera_data as unknown as Camera,
      accessories: accessories?.map(a => a.accessory_data as unknown as AccessorySpec) || [],
      rigStyle: dbRig.rig_style,
      autoAddedCables: (dbRig.auto_added_cables as Record<string, string[]>) || {},
    });
  },
};
